import type { ReactNode, Ref, TextareaHTMLAttributes } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Hint } from './Hint';
import { Label } from './Label';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy } from './cx';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  id: string;
  label: ReactNode;
  labelClassName?: string;
  labelLang?: string;
  isPageHeading?: boolean;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  formGroupClassName?: string;
  extraDescribedBy?: string;
  beforeInput?: ReactNode;
  /** Rendered after the textarea inside the form group (used by CharacterCount). */
  after?: ReactNode;
  ref?: Ref<HTMLTextAreaElement>;
}

/** govuk/components/textarea */
export function Textarea({
  id,
  label,
  labelClassName,
  labelLang,
  isPageHeading,
  hint,
  hintLang,
  error,
  required,
  formGroupClassName,
  extraDescribedBy,
  beforeInput,
  after,
  className,
  rows = 5,
  ref,
  ...rest
}: TextareaProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Label htmlFor={id} className={labelClassName} isPageHeading={isPageHeading} lang={labelLang}>
        {label}
        <RequiredMarker required={required} />
      </Label>
      {hint && (
        <Hint id={hintId} lang={hintLang}>
          {hint}
        </Hint>
      )}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      {beforeInput}
      <textarea
        ref={ref}
        className={cx('govuk-textarea', error && 'govuk-textarea--error', className)}
        id={id}
        rows={rows}
        aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {after}
    </div>
  );
}
